import React, { useState, useEffect } from 'react';
import {
  Cog6ToothIcon,
  BellIcon,
  KeyIcon,
  ShieldCheckIcon,
  ServerIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import axios from 'axios';

export default function Settings() {
  // API base URL with fallback for production
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    appName: 'Clarity UPSC',
    adminEmail: 'admin@clarity.app',
    notificationDefaults: {
      priority: 'normal',
      ttl: 86400,
      sound: true,
      vibration: true,
    },
    apiLimits: {
      maxNotificationsPerDay: 1000,
      maxUsersPerBatch: 500,
      maxFileSize: 10,
    },
    security: {
      requireTwoFactor: false,
      sessionTimeout: 480,
      ipWhitelist: [],
    },
  });

  // Maintenance mode state
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('Server under maintenance. We are upgrading our systems and will be back shortly!');
  const [isLoadingMaintenance, setIsLoadingMaintenance] = useState(false);

  // Force update state
  const [minAppVersion, setMinAppVersion] = useState('3.8.0');
  const [isLoadingVersion, setIsLoadingVersion] = useState(false);

  // Load maintenance status on mount
  useEffect(() => {
    loadMaintenanceStatus();
  }, []);

  const loadMaintenanceStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/system/status`);
      setMaintenanceMode(response.data.maintenance_mode);
      if (response.data.message) {
        setMaintenanceMessage(response.data.message);
      }
      if (response.data.min_app_version) {
        setMinAppVersion(response.data.min_app_version);
      }
    } catch (error) {
      console.error('Failed to load maintenance status:', error);
    }
  };

  const updateMinVersion = async () => {
    if (!confirm(`Set minimum app version to ${minAppVersion}? Users below this version will be forced to update.`)) {
      return;
    }

    setIsLoadingVersion(true);
    try {
      await axios.post(
        `${API_BASE_URL}/system/min-version`,
        { min_version: minAppVersion },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );
      toast.success(`Minimum app version set to ${minAppVersion}`);
    } catch (error: any) {
      console.error('Version update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update minimum version');
    } finally {
      setIsLoadingVersion(false);
    }
  };

  const toggleMaintenanceMode = async () => {
    if (!confirm(`Are you sure you want to ${maintenanceMode ? 'DISABLE' : 'ENABLE'} maintenance mode?`)) {
      return;
    }

    setIsLoadingMaintenance(true);
    try {
      await axios.post(
        `${API_BASE_URL}/system/maintenance`,
        {
          enabled: !maintenanceMode,
          message: maintenanceMessage
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );

      setMaintenanceMode(!maintenanceMode);
      toast.success(`Maintenance mode ${!maintenanceMode ? 'ENABLED' : 'DISABLED'}`);
    } catch (error: any) {
      console.error('Maintenance toggle error:', error);
      toast.error(error.response?.data?.detail || error.response?.data?.message || 'Failed to toggle maintenance mode');
    } finally {
      setIsLoadingMaintenance(false);
    }
  };

  const tabs = [
    { id: 'general', name: 'General', icon: Cog6ToothIcon },
    { id: 'system', name: 'System', icon: WrenchScrewdriverIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
    { id: 'security', name: 'Security', icon: ShieldCheckIcon },
    { id: 'api', name: 'API & Limits', icon: ServerIcon },
    { id: 'password', name: 'Password', icon: KeyIcon },
    { id: 'logs', name: 'Activity Logs', icon: DocumentTextIcon },
  ];

  const handleSave = () => {
    toast.success('Settings saved successfully');
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-6 p-6">
      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Manage admin dashboard configuration
        </p>
      </div>

      <div className="flex gap-8">
        {/* Sidebar tabs */}
        <div className="w-64 space-y-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-left transition-colors ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span className="font-medium">{tab.name}</span>
            </button>
          ))}
        </div>

        {/* Content area */}
        <div className="flex-1">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            {activeTab === 'general' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  General Settings
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Application Name
                  </label>
                  <input
                    type="text"
                    value={settings.appName}
                    onChange={(e) => setSettings({...settings, appName: e.target.value})}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Admin Email
                  </label>
                  <input
                    type="email"
                    value={settings.adminEmail}
                    onChange={(e) => setSettings({...settings, adminEmail: e.target.value})}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Time Zone
                  </label>
                  <select className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white">
                    <option>Asia/Kolkata (IST)</option>
                    <option>UTC</option>
                    <option>America/New_York (EST)</option>
                  </select>
                </div>
              </div>
            )}

            {activeTab === 'system' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  System Management
                </h3>

                {/* Maintenance Mode Section */}
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <WrenchScrewdriverIcon className="w-6 h-6 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-1">
                        ⚠️ Maintenance Mode
                      </h4>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        When enabled, all users will see a maintenance screen instead of the app.
                        Admin access remains available to disable maintenance mode.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Toggle Switch */}
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Maintenance Mode
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Currently: <span className={`font-semibold ${maintenanceMode ? 'text-red-600' : 'text-green-600'}`}>
                        {maintenanceMode ? 'ENABLED' : 'DISABLED'}
                      </span>
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={maintenanceMode}
                      onChange={toggleMaintenanceMode}
                      disabled={isLoadingMaintenance}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all dark:border-gray-600 peer-checked:bg-red-600"></div>
                  </label>
                </div>

                {/* Maintenance Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Maintenance Message
                  </label>
                  <textarea
                    rows={4}
                    value={maintenanceMessage}
                    onChange={(e) => setMaintenanceMessage(e.target.value)}
                    placeholder="Enter the message users will see during maintenance..."
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    This message will be displayed to users when maintenance mode is active.
                  </p>
                </div>

                {/* Apply Button */}
                <button
                  onClick={async () => {
                    setIsLoadingMaintenance(true);
                    try {
                      await axios.post(
                        `${API_BASE_URL}/system/maintenance`,
                        {
                          enabled: maintenanceMode,
                          message: maintenanceMessage
                        },
                        {
                          headers: {
                            Authorization: `Bearer ${localStorage.getItem('adminToken')}`
                          }
                        }
                      );
                      toast.success('Maintenance message updated');
                    } catch (error: any) {
                      console.error('Message update error:', error);
                      toast.error(error.response?.data?.detail || error.response?.data?.message || 'Failed to update message');
                    } finally {
                      setIsLoadingMaintenance(false);
                    }
                  }}
                  disabled={isLoadingMaintenance}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50"
                >
                  {isLoadingMaintenance ? 'Updating...' : 'Update Message'}
                </button>

                {/* Divider */}
                <div className="border-t border-gray-200 dark:border-gray-700 my-8"></div>

                {/* Force Update Section */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <ServerIcon className="w-6 h-6 text-blue-600 dark:text-blue-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                        🔄 Force Update
                      </h4>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Users with app versions below the minimum will see a mandatory update screen.
                        Use semantic versioning (MAJOR.MINOR.PATCH).
                      </p>
                    </div>
                  </div>
                </div>

                {/* Min Version Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Minimum App Version (Force Update)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={minAppVersion}
                      onChange={(e) => setMinAppVersion(e.target.value)}
                      placeholder="e.g., 3.9.0"
                      pattern="^\d+\.\d+\.\d+$"
                      className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
                    />
                    <button
                      onClick={updateMinVersion}
                      disabled={isLoadingVersion}
                      className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50"
                    >
                      {isLoadingVersion ? 'Updating...' : 'Apply'}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Current setting: <span className="font-semibold">{minAppVersion}</span>
                    <br />
                    Users on versions below this will be required to update before using the app.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Notification Defaults
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Default Priority
                  </label>
                  <select 
                    value={settings.notificationDefaults.priority}
                    onChange={(e) => setSettings({
                      ...settings,
                      notificationDefaults: {...settings.notificationDefaults, priority: e.target.value}
                    })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Time to Live (seconds)
                  </label>
                  <input
                    type="number"
                    value={settings.notificationDefaults.ttl}
                    onChange={(e) => setSettings({
                      ...settings,
                      notificationDefaults: {...settings.notificationDefaults, ttl: parseInt(e.target.value)}
                    })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
                  />
                </div>

                <div className="space-y-3">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.notificationDefaults.sound}
                      onChange={(e) => setSettings({
                        ...settings,
                        notificationDefaults: {...settings.notificationDefaults, sound: e.target.checked}
                      })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Enable sound by default</span>
                  </label>

                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.notificationDefaults.vibration}
                      onChange={(e) => setSettings({
                        ...settings,
                        notificationDefaults: {...settings.notificationDefaults, vibration: e.target.checked}
                      })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Enable vibration by default</span>
                  </label>
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Security Settings
                </h3>

                <div>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.security.requireTwoFactor}
                      onChange={(e) => setSettings({
                        ...settings,
                        security: {...settings.security, requireTwoFactor: e.target.checked}
                      })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Require two-factor authentication
                    </span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Session Timeout (minutes)
                  </label>
                  <input
                    type="number"
                    value={settings.security.sessionTimeout}
                    onChange={(e) => setSettings({
                      ...settings,
                      security: {...settings.security, sessionTimeout: parseInt(e.target.value)}
                    })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    IP Whitelist (one per line)
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Leave empty to allow all IPs"
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
                  />
                </div>
              </div>
            )}

            {activeTab === 'password' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Change Password
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
                  />
                </div>

                <button
                  onClick={() => toast.success('Password changed successfully')}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow"
                >
                  Update Password
                </button>
              </div>
            )}

            {/* Save button for non-password tabs */}
            {activeTab !== 'password' && activeTab !== 'logs' && activeTab !== 'system' && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow"
                >
                  Save Settings
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}